// NEW_FILE_JUSTIFIED: Yes/no pill-toggle with conditional sub-fields or textarea for policy application forms — no existing conditional detail field component
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { FieldRenderer } from './FieldRenderer';
import type { IConditionalDetailFieldProps } from '@/@types/policyFormTemplates';

export function ConditionalDetailField({
  field,
  value,
  onChange,
  disabled,
}: IConditionalDetailFieldProps) {
  const handleAnswerChange = (answer: boolean) => {
    onChange({ ...value, answer });
  };

  const handleDetailsChange = (details: string) => {
    onChange({ ...value, details });
  };

  const handleSubFieldChange = (key: string, val: unknown) => {
    onChange({
      ...value,
      subFieldValues: { ...value.subFieldValues, [key]: val },
    });
  };

  return (
    <div className="space-y-3 rounded-lg border p-4">
      <span className="text-sm leading-relaxed">
        {field.label}
        {field.required && <span className="text-destructive ml-1">*</span>}
      </span>

      <div className="flex gap-3">
        {(['yes', 'no'] as const).map(opt => {
          const isSelected =
            (opt === 'yes' && value.answer === true) || (opt === 'no' && value.answer === false);
          return (
            <button
              key={opt}
              type="button"
              onClick={() => !disabled && handleAnswerChange(opt === 'yes')}
              disabled={disabled}
              className={cn(
                'flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-medium border transition-colors',
                isSelected
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-background border-border text-foreground hover:bg-muted'
              )}
            >
              <span
                className={cn(
                  'size-4 rounded-full border-2 flex items-center justify-center shrink-0',
                  isSelected ? 'border-primary-foreground' : 'border-muted-foreground'
                )}
              >
                {isSelected && <span className="size-2 rounded-full bg-primary-foreground" />}
              </span>
              {opt === 'yes' ? 'Yes' : 'No'}
            </button>
          );
        })}
      </div>

      {value.answer && (
        <div className="ml-4 space-y-3 border-l-2 border-muted pl-4">
          {field.subFields && field.subFields.length > 0 ? (
            field.subFields.map(subField => (
              <FieldRenderer
                key={subField.key}
                field={subField}
                value={value.subFieldValues?.[subField.key]}
                onChange={(key, val) => handleSubFieldChange(key, val)}
                allValues={value.subFieldValues ?? {}}
                disabled={disabled}
              />
            ))
          ) : (
            <Textarea
              value={value.details || ''}
              onChange={e => handleDetailsChange(e.target.value)}
              placeholder="Please provide details..."
              disabled={disabled}
              rows={3}
            />
          )}
        </div>
      )}
    </div>
  );
}
